/**
 * Single source of truth for the API origin.
 *
 * VITE_API_URL points at the `/api` mount (see frontend/.env.example and the
 * deploy table in README). The version segment lives here rather than in every
 * endpoint string: every path in docs/api/openapi.json is `/api/v1/...`, and
 * leaving `/v1` out is what made the previous data layer 404 on every call.
 */
export const API_BASE = `${import.meta.env.VITE_API_URL || '/api'}/v1`;

/**
 * Pull a human-readable message out of an error response.
 *
 * FastAPI reports failures as `{"detail": ...}` — either a string or, for
 * request-validation errors, a list of per-field objects. Reading only
 * `message` (as the client used to) turned every backend error into a generic
 * "An error occurred".
 */
export async function readErrorMessage(response: Response, fallback: string): Promise<string> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return response.statusText || fallback;
  }
  if (!body || typeof body !== 'object') return fallback;

  const { detail, message, error } = body as {
    detail?: unknown;
    message?: unknown;
    error?: unknown;
  };
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => (item && typeof item === 'object' ? (item as { msg?: unknown }).msg : null))
      .filter((msg): msg is string => typeof msg === 'string');
    if (messages.length) return messages.join('; ');
  }
  if (typeof message === 'string') return message;
  if (typeof error === 'string') return error;
  return fallback;
}
